import { useEffect, useState } from "react";
import { BffClient } from "@made-people/gears-bff-client";
import { frontendProductToGreaseProduct } from "../../../services/bff/mappers/frontend-product-to-grease-product";
import { useAllocatedWarehouseIds } from "../../../services/centra/allocation-rule/use-allocated-warehouse-ids";
import type { FrontendContextV1 } from "@made-people/nestjs-bff-service";
import type { StylePickerItem } from "../settings";
import type { StylePickerStyle } from "./types";

export const useStyleProducts = (params: {
  enabled: boolean;
  stylePickerItems: StylePickerItem[];
  bffClient: BffClient;
  frontendContext: FrontendContextV1;
}) => {
  const [styles, setStyles] = useState<StylePickerStyle[]>([]);
  const allocatedWarehouseIds = useAllocatedWarehouseIds();

  useEffect(() => {
    let cancelled = false;

    const getProduct = async (productId: string) => {
      const result = await params.bffClient.fetchFrontendProductsCached(
        [productId],
        params.frontendContext,
      );
      if (!result?.frontendProducts.length) {
        return undefined;
      }
      return frontendProductToGreaseProduct(
        result.frontendProducts[0],
        params.frontendContext,
        allocatedWarehouseIds,
      );
    };

    const getStyleProducts = async () => {
      if (!params.enabled) {
        setStyles([]);
        return;
      }

      if (!params.stylePickerItems.length) {
        setStyles([]);
        return;
      }

      const results = await Promise.all(
        params.stylePickerItems.map(async (item) => {
          const productId = String(item.productId);
          const product = await getProduct(productId);
          if (!product) {
            return undefined;
          }
          return { item, product };
        }),
      );

      if (!cancelled) {
        setStyles(
          results.filter(
            (style): style is StylePickerStyle => style !== undefined,
          ),
        );
      }
    };

    getStyleProducts();

    return () => {
      cancelled = true;
    };
  }, [
    params.bffClient,
    params.enabled,
    params.frontendContext,
    params.stylePickerItems,
    allocatedWarehouseIds,
  ]);

  return styles;
};
